import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, BookOpen, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [coursesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/categories`)
      ]);
      setCourses(coursesRes.data || []);
      setCategories(categoriesRes.data.courses || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
      setError('Unable to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          course.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      {/* Hero */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#2D4A5E] flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#1A202C]">Courses</h1>
          </div>
          <p className="text-[#64748B] max-w-2xl">
            Transform your financial knowledge with our expert-led video courses. From budgeting basics to advanced investment strategies.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 lg:top-20 z-40 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-[#E2E8F0] focus:border-[#2D4A5E] focus:ring-[#2D4A5E]/20 rounded-lg h-12"
                data-testid="course-search-input"
              />
            </div>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full sm:w-48 h-12 appearance-none bg-white border border-[#E2E8F0] rounded-lg px-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4A5E]/20 focus:border-[#2D4A5E]"
                data-testid="category-filter"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-[#64748B]">
              Showing <span className="font-medium text-[#1A202C]">{filteredCourses.length}</span> courses
            </p>
            {selectedCategory !== 'all' && (
              <Badge variant="secondary" className="gap-2">
                {selectedCategory}
                <button onClick={() => handleCategoryChange('all')} className="hover:text-red-500">×</button>
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white animate-pulse rounded-xl h-[400px]"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 mx-auto text-red-300 mb-4" />
              <h3 className="text-xl font-semibold text-[#1A202C] mb-2">Error Loading Courses</h3>
              <p className="text-[#64748B] mb-4">{error}</p>
              <Button 
                onClick={fetchData}
                className="bg-[#2D4A5E] hover:bg-[#1A202C]"
              >
                Try Again
              </Button>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 mx-auto text-[#CBD5E1] mb-4" />
              <h3 className="text-xl font-semibold text-[#1A202C] mb-2">No courses found</h3>
              <p className="text-[#64748B]">Try adjusting your search or filter criteria</p>
              <Button 
                onClick={() => { setSearchQuery(''); handleCategoryChange('all'); }}
                variant="outline" 
                className="mt-4"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCourses.map(course => (
                <ProductCard key={course.product_id} product={course} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoursesPage;
